import { useState, useEffect, useRef } from 'react'
import { RefreshCw, Save, AlertTriangle, CheckCircle2, XCircle, Database, Radio } from 'lucide-react'
import { AppIcon, IconLabel } from '@/components/ui/app-icon'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { useToast } from '@/hooks/useToast'
import { settingsApi } from '@/lib/api'
import { FolderBrowser } from '@/components/ui/folder-browser'
import { Barcode } from 'lucide-react'

interface InfraSettings {
  gs1_company_prefix: string
  backend: string
  mongodb_uri: string
  mongodb_db_name: string
  mongodb_pool_size: number
  mongodb_timeout: number
  mongodb_healthy?: boolean
  postgres_uri: string
  postgres_pool_min: number
  postgres_pool_max: number
  postgres_timeout: number
  postgres_healthy?: boolean
  redis_url: string
  redis_enabled: boolean
  redis_healthy?: boolean
  restart_required?: boolean
}

const DEFAULT: InfraSettings = {
  gs1_company_prefix: '',
  backend: 'json',
  mongodb_uri: 'mongodb://localhost:27017/',
  mongodb_db_name: 'truemark',
  mongodb_pool_size: 10,
  mongodb_timeout: 5000,
  postgres_uri: 'postgresql://postgres:admin@127.0.0.1:5432/truemark',
  postgres_pool_min: 2,
  postgres_pool_max: 10,
  postgres_timeout: 30,
  redis_url: '',
  redis_enabled: false,
}

export function InfrastructurePage() {
  const { toast } = useToast()
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [settings, setSettings] = useState<InfraSettings>(DEFAULT)
  const [mongoTestResult, setMongoTestResult] = useState<{ ok: boolean; message: string } | null>(null)
  const [postgresTestResult, setPostgresTestResult] = useState<{ ok: boolean; message: string } | null>(null)
  const [redisTestResult, setRedisTestResult] = useState<{ ok: boolean; message: string } | null>(null)
  const [isTestingMongo, setIsTestingMongo] = useState(false)
  const [isTestingPostgres, setIsTestingPostgres] = useState(false)
  const [isTestingRedis, setIsTestingRedis] = useState(false)
  const [isMigrating, setIsMigrating] = useState(false)
  const [migrateDataDir, setMigrateDataDir] = useState('')
  const [migrateOutput, setMigrateOutput] = useState('')
  const [folderBrowserOpen, setFolderBrowserOpen] = useState(false)
  const [saveWarnings, setSaveWarnings] = useState<string[]>([])

  // Keep toast in a ref so load() doesn't become a dep that changes every render
  const toastRef = useRef(toast)
  toastRef.current = toast

  const load = async () => {
    setIsLoading(true)
    try {
      const res = await settingsApi.getDbSettings()
      const data = res.data as InfraSettings
      setSettings({ ...DEFAULT, ...data })
      setMongoTestResult(null)
      setPostgresTestResult(null)
      setRedisTestResult(null)
      setSaveWarnings([])
    } catch {
      toastRef.current.error('Ошибка загрузки настроек инфраструктуры')
    } finally {
      setIsLoading(false)
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load() }, [])

  const update = <K extends keyof InfraSettings>(key: K, value: InfraSettings[K]) => {
    setSettings(s => ({ ...s, [key]: value }))
    // Сбрасываем результаты тестов при изменении
    if (key === 'mongodb_uri' || key === 'mongodb_db_name') setMongoTestResult(null)
    if (key === 'postgres_uri') setPostgresTestResult(null)
    if (key === 'redis_url') setRedisTestResult(null)
  }

  // ── Тесты подключений ──

  const handleTestMongo = async () => {
    setIsTestingMongo(true)
    setMongoTestResult(null)
    try {
      const res = await settingsApi.testMongo({
        uri: settings.mongodb_uri,
        db_name: settings.mongodb_db_name,
      })
      const data = res.data as { ok: boolean; message: string; collections?: number; documents?: number }
      setMongoTestResult({
        ok: data.ok,
        message: data.ok
          ? `${data.message} (${data.collections} коллекций, ${data.documents} документов)`
          : data.message,
      })
    } catch (err: unknown) {
      setMongoTestResult({ ok: false, message: (err as Error).message || 'Ошибка подключения' })
    } finally {
      setIsTestingMongo(false)
    }
  }

  const handleTestPostgres = async () => {
    setIsTestingPostgres(true)
    setPostgresTestResult(null)
    try {
      const res = await settingsApi.testPostgres({ uri: settings.postgres_uri })
      const data = res.data as { ok: boolean; message: string; version?: string }
      setPostgresTestResult({
        ok: data.ok,
        message: data.ok && data.version
          ? `${data.message} (${data.version.split(',')[0]})`
          : data.message,
      })
    } catch (err: unknown) {
      setPostgresTestResult({ ok: false, message: (err as Error).message || 'Ошибка подключения' })
    } finally {
      setIsTestingPostgres(false)
    }
  }

  const handleTestRedis = async () => {
    setIsTestingRedis(true)
    setRedisTestResult(null)
    try {
      const res = await settingsApi.testRedis({ url: settings.redis_url })
      const data = res.data as { ok: boolean; message: string; version?: string }
      setRedisTestResult({
        ok: data.ok,
        message: data.message,
      })
    } catch (err: unknown) {
      setRedisTestResult({ ok: false, message: (err as Error).message || 'Ошибка подключения' })
    } finally {
      setIsTestingRedis(false)
    }
  }

  // ── Сохранение ──

  const handleSave = async () => {
    setIsSaving(true)
    setSaveWarnings([])
    try {
      const res = await settingsApi.saveDbSettings({
        gs1_company_prefix: settings.gs1_company_prefix,
        backend: settings.backend,
        mongodb_uri: settings.mongodb_uri,
        mongodb_db_name: settings.mongodb_db_name,
        mongodb_pool_size: settings.mongodb_pool_size,
        mongodb_timeout: settings.mongodb_timeout,
        postgres_uri: settings.postgres_uri,
        postgres_pool_min: settings.postgres_pool_min,
        postgres_pool_max: settings.postgres_pool_max,
        postgres_timeout: settings.postgres_timeout,
        redis_enabled: settings.redis_enabled,
        redis_url: settings.redis_url,
      })
      const data = res.data as { success: boolean; message: string; warnings?: string[] }
      if (data.success) {
        if (data.warnings && data.warnings.length > 0) {
          setSaveWarnings(data.warnings)
          toast.warning(data.message)
        } else {
          toast.success(data.message)
        }
      } else {
        toast.error(data.message || 'Ошибка сохранения')
      }
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
        || (err as Error).message || 'Ошибка сохранения'
      toast.error(msg)
    } finally {
      setIsSaving(false)
    }
  }

  // ── Миграция ──

  const handleMigrate = async () => {
    if (!confirm('Мигрировать данные JSON → MongoDB? Этот процесс может занять время.')) return
    setIsMigrating(true)
    setMigrateOutput('')
    try {
      const payload: { uri: string; db_name: string; data_dir?: string } = {
        uri: settings.mongodb_uri || 'mongodb://localhost:27017/',
        db_name: settings.mongodb_db_name || 'truemark',
      }
      if (migrateDataDir.trim()) payload.data_dir = migrateDataDir.trim()
      const res = await settingsApi.migrate(payload)
      const data = res.data as { ok: boolean; output?: string }
      if (data.ok) {
        toast.success('Миграция завершена')
      } else {
        toast.error('Ошибка миграции')
      }
      setMigrateOutput(data.output ?? '')
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { output?: string } }; message?: string })?.response?.data?.output
        || (err as Error).message || 'Неизвестная ошибка'
      toast.error('Ошибка миграции')
      setMigrateOutput(msg)
    } finally {
      setIsMigrating(false)
    }
  }

  if (isLoading) return <LoadingSpinner className="mt-20" />

  return (
    <div className="max-w-3xl">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900"><IconLabel icon="infrastructure">Инфраструктура</IconLabel></h1>
        <Button variant="ghost" size="sm" onClick={load}><RefreshCw className="w-4 h-4" /></Button>
      </div>

      {/* ── Баннер: нужен рестарт ── */}
      {settings.restart_required && (
        <div className="mb-6 flex items-start gap-3 bg-amber-50 border border-amber-300 rounded-lg p-4">
          <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-semibold text-amber-800">Требуется перезапуск сервера</p>
            <p className="text-sm text-amber-700 mt-1">
              Сохранённые настройки отличаются от текущих. Перезапустите сервер (или контейнер) для применения.
            </p>
          </div>
        </div>
      )}

      <div className="space-y-6">
        {/* ══════════ GS1 Префикс компании ══════════ */}
        <Section title="GS1 Префикс компании" icon={<Barcode className="w-5 h-5" />}>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Префикс компании (первые 7–10 цифр GLN):</label>
              <Input
                value={settings.gs1_company_prefix}
                onChange={e => {
                  const v = e.target.value.replace(/\D/g, '').slice(0, 10)
                  update('gs1_company_prefix', v)
                }}
                placeholder="46207654"
                className="font-mono text-sm max-w-xs"
                maxLength={10}
              />
              <p className="text-xs text-gray-500 mt-1">
                Первые 7–10 цифр вашего GLN (Global Location Number). Используется для генерации SSCC-кодов коробок и паллет.
                Полный GLN — 13 цифр; здесь указывается только префикс компании без серийной части и контрольной цифры.
              </p>
            </div>
            {settings.gs1_company_prefix && (settings.gs1_company_prefix.length < 7 || settings.gs1_company_prefix.length > 10) && (
              <div className="flex items-start gap-2 p-2 rounded text-sm bg-amber-50 text-amber-800">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>Длина префикса должна быть от 7 до 10 цифр. Текущая: {settings.gs1_company_prefix.length}</span>
              </div>
            )}
          </div>
        </Section>

        {/* ══════════ Бэкенд хранения ══════════ */}
        <Section title={<IconLabel icon="infrastructure">Бэкенд хранения данных</IconLabel>} icon={<Database className="w-5 h-5" />}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Хранилище:</label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <BackendCard
                  active={settings.backend === 'json'}
                  onClick={() => update('backend', 'json')}
                  icon={<AppIcon name="folder" className="h-6 w-6" />}
                  title="JSON файлы"
                  description="Локальные файлы, без внешних зависимостей. Подходит для одного инстанса."
                />
                <BackendCard
                  active={settings.backend === 'mongodb'}
                  onClick={() => update('backend', 'mongodb')}
                  icon={<Database className="h-6 w-6" />}
                  title="MongoDB"
                  description="Document-store, несколько инстансов. Legacy prod backend."
                />
                <BackendCard
                  active={settings.backend === 'postgres'}
                  onClick={() => update('backend', 'postgres')}
                  icon={<span className="text-lg font-bold tracking-tight">PG</span>}
                  title="PostgreSQL"
                  description="Нормализованная схема marking. Целевой prod backend (phase 1)."
                />
              </div>
            </div>

            {settings.backend === 'mongodb' && (
              <div className="space-y-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">MongoDB URI:</label>
                  <Input
                    value={settings.mongodb_uri}
                    onChange={e => update('mongodb_uri', e.target.value)}
                    placeholder="mongodb://localhost:27017/"
                    className="font-mono text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Имя базы данных:</label>
                  <Input
                    value={settings.mongodb_db_name}
                    onChange={e => update('mongodb_db_name', e.target.value)}
                    placeholder="truemark"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Pool Size:</label>
                    <Input
                      type="number"
                      min={1}
                      max={100}
                      value={String(settings.mongodb_pool_size)}
                      onChange={e => update('mongodb_pool_size', parseInt(e.target.value) || 10)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Timeout (мс):</label>
                    <Input
                      type="number"
                      min={1000}
                      max={30000}
                      value={String(settings.mongodb_timeout)}
                      onChange={e => update('mongodb_timeout', parseInt(e.target.value) || 5000)}
                    />
                  </div>
                </div>
                <div>
                  <Button variant="secondary" size="sm" onClick={handleTestMongo} isLoading={isTestingMongo}>
                    <Database className="w-4 h-4 mr-1" /> Проверить подключение к MongoDB
                  </Button>
                  {mongoTestResult && (
                    <TestResult ok={mongoTestResult.ok} message={mongoTestResult.message} />
                  )}
                </div>

                {settings.mongodb_healthy !== undefined && (
                  <HealthBadge healthy={settings.mongodb_healthy} label="MongoDB" />
                )}
              </div>
            )}

            {settings.backend === 'postgres' && (
              <div className="space-y-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">PostgreSQL URI:</label>
                  <Input
                    value={settings.postgres_uri}
                    onChange={e => update('postgres_uri', e.target.value)}
                    placeholder="postgresql://postgres:admin@127.0.0.1:5432/truemark"
                    className="font-mono text-sm"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Формат: postgresql://user:password@host:port/database
                  </p>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Pool min:</label>
                    <Input
                      type="number"
                      min={1}
                      max={50}
                      value={String(settings.postgres_pool_min)}
                      onChange={e => update('postgres_pool_min', parseInt(e.target.value) || 2)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Pool max:</label>
                    <Input
                      type="number"
                      min={1}
                      max={100}
                      value={String(settings.postgres_pool_max)}
                      onChange={e => update('postgres_pool_max', parseInt(e.target.value) || 10)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Timeout (с):</label>
                    <Input
                      type="number"
                      min={5}
                      max={120}
                      value={String(settings.postgres_timeout)}
                      onChange={e => update('postgres_timeout', parseInt(e.target.value) || 30)}
                    />
                  </div>
                </div>
                <div>
                  <Button variant="secondary" size="sm" onClick={handleTestPostgres} isLoading={isTestingPostgres}>
                    <Database className="w-4 h-4 mr-1" /> Проверить подключение к PostgreSQL
                  </Button>
                  {postgresTestResult && (
                    <TestResult ok={postgresTestResult.ok} message={postgresTestResult.message} />
                  )}
                </div>

                {settings.postgres_healthy !== undefined && (
                  <HealthBadge healthy={settings.postgres_healthy} label="PostgreSQL" />
                )}

                <p className="text-xs text-gray-500">
                  После первой установки PG запустите на сервере:{' '}
                  <code className="bg-gray-100 px-1 rounded">python scripts/init_postgres_databases.py</code>
                </p>
              </div>
            )}
          </div>
        </Section>

        {/* ══════════ Redis ══════════ */}
        <Section title={<IconLabel icon="queue">Redis (кэш, агенты, очереди)</IconLabel>} icon={<Radio className="w-5 h-5" />}>
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={settings.redis_enabled}
                  onChange={e => update('redis_enabled', e.target.checked)}
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600" />
              </label>
              <span className="text-sm font-medium text-gray-700">
                {settings.redis_enabled ? 'Redis включён' : 'Redis выключен (in-memory)'}
              </span>
            </div>

            {!settings.redis_enabled && (
              <div className="text-sm text-gray-500 bg-gray-50 rounded-lg p-3">
                Без Redis агенты и посты хранятся в памяти процесса.
                Подходит для одного инстанса. Для нескольких серверов включите Redis.
              </div>
            )}

            {settings.redis_enabled && (
              <div className="space-y-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Redis URL:</label>
                  <Input
                    value={settings.redis_url}
                    onChange={e => update('redis_url', e.target.value)}
                    placeholder="redis://localhost:6379/0"
                    className="font-mono text-sm"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Формат: redis://[[username:]password@]host[:port][/db]
                  </p>
                </div>
                <div>
                  <Button variant="secondary" size="sm" onClick={handleTestRedis} isLoading={isTestingRedis}>
                    <Radio className="w-4 h-4 mr-1" /> Проверить подключение к Redis
                  </Button>
                  {redisTestResult && (
                    <TestResult ok={redisTestResult.ok} message={redisTestResult.message} />
                  )}
                </div>

                {settings.redis_healthy !== undefined && (
                  <HealthBadge healthy={settings.redis_healthy} label="Redis" />
                )}
              </div>
            )}
          </div>
        </Section>

        {/* ══════════ Миграция ══════════ */}
        <Section title={<IconLabel icon="tasks">Миграция JSON → MongoDB</IconLabel>}>
          <div className="space-y-3">
            <p className="text-sm text-gray-600">
              Перенос данных из JSON файлов в MongoDB. Укажите путь к папке с JSON и целевую MongoDB.
            </p>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Путь к папке JSON-хранилища:
              </label>
              <div className="flex gap-2">
                <Input
                  className="flex-1 font-mono text-sm"
                  value={migrateDataDir}
                  onChange={e => setMigrateDataDir(e.target.value)}
                  placeholder="Нажмите 'Обзор' или введите путь вручную"
                />
                <Button variant="secondary" size="sm" onClick={() => setFolderBrowserOpen(true)}>
                  <AppIcon name="folder" className="w-4 h-4 mr-1" /> Обзор...
                </Button>
              </div>
            </div>
            <Button variant="primary" size="sm" onClick={handleMigrate} isLoading={isMigrating} disabled={!migrateDataDir.trim()}>
              <Database className="w-4 h-4 mr-1" /> Запустить миграцию
            </Button>

            <FolderBrowser
              isOpen={folderBrowserOpen}
              onClose={() => setFolderBrowserOpen(false)}
              onSelect={(path) => setMigrateDataDir(path)}
              title="Выберите папку JSON-хранилища"
            />
            {migrateOutput && (
              <pre className="mt-2 p-3 bg-white border rounded text-xs text-gray-800 whitespace-pre-wrap max-h-64 overflow-y-auto">
                {migrateOutput}
              </pre>
            )}
          </div>
        </Section>

        {/* ── Предупреждения ── */}
        {saveWarnings.length > 0 && (
          <div className="bg-amber-50 border border-amber-300 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-amber-800 flex items-center gap-1">
              <AlertTriangle className="w-4 h-4" /> Предупреждения при сохранении
            </h4>
            <ul className="mt-2 list-disc pl-5 text-sm text-amber-700 space-y-1">
              {saveWarnings.map((w, i) => <li key={i}>{w}</li>)}
            </ul>
          </div>
        )}

        {/* ── Кнопка сохранения ── */}
        <div className="flex justify-center pt-2 pb-4">
          <Button onClick={handleSave} isLoading={isSaving} size="lg">
            <Save className="w-4 h-4 mr-1" /> Сохранить и применить после рестарта
          </Button>
        </div>

        {/* ── Информация ── */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
          <strong>Как это работает:</strong>
          <ul className="mt-2 list-disc pl-5 space-y-1">
            <li>Настройки сохраняются в <code className="bg-blue-100 px-1 rounded">data/infrastructure.json</code> — приоритетнее Docker env vars</li>
            <li>Переключение бэкенда требует <strong>перезапуска</strong> сервера (или контейнера)</li>
            <li>Если MongoDB/PostgreSQL/Redis недоступны при старте — сервер автоматически переключится на JSON/in-memory</li>
            <li>Валидация подключения выполняется при сохранении</li>
          </ul>
        </div>
      </div>
    </div>
  )
}

// ── Компоненты ──

function Section({ title, icon, children }: { title: React.ReactNode; icon?: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="bg-white rounded-lg shadow p-6">
      <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
        {icon}
        {title}
      </h2>
      {children}
    </section>
  )
}

function BackendCard({ active, onClick, icon, title, description }: {
  active: boolean
  onClick: () => void
  icon: React.ReactNode
  title: string
  description: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-left p-4 rounded-lg border-2 transition-all ${
        active
          ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-200'
          : 'border-gray-200 bg-white hover:border-gray-300'
      }`}
    >
      <div className="text-2xl mb-1 text-gray-700">{icon}</div>
      <div className="font-semibold text-sm text-gray-900">{title}</div>
      <div className="text-xs text-gray-500 mt-1">{description}</div>
      {active && (
        <div className="mt-2">
          <span className="inline-flex items-center text-xs font-medium text-blue-700 bg-blue-100 rounded-full px-2 py-0.5">
            <CheckCircle2 className="w-3 h-3 mr-1" /> Выбран
          </span>
        </div>
      )}
    </button>
  )
}

function TestResult({ ok, message }: { ok: boolean; message: string }) {
  return (
    <div className={`mt-2 flex items-start gap-2 p-2 rounded text-sm ${
      ok ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
    }`}>
      {ok ? <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" /> : <XCircle className="w-4 h-4 mt-0.5 shrink-0" />}
      <span>{message}</span>
    </div>
  )
}

function HealthBadge({ healthy, label }: { healthy: boolean; label: string }) {
  return (
    <div className="flex items-center gap-2">
      <span className={`w-2.5 h-2.5 rounded-full ${healthy ? 'bg-green-500' : 'bg-red-500'}`} />
      <span className={`text-sm font-medium ${healthy ? 'text-green-700' : 'text-red-700'}`}>
        {label}: {healthy ? 'подключён' : 'недоступен'}
      </span>
    </div>
  )
}
